import React, {useState, useEffect, useCallback} from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import {MoreHorizontal, Trash2} from 'lucide-react';
import SearchGreyIcon from '../../assets/search-grey.svg';
import {SettingsCard} from '../../components/SettingsCard';
import {useAuthFrontendApis} from '@propelauth/frontend-apis-react';
import {useAuthInfo} from '@propelauth/react';
import {useSelector} from 'react-redux';
import Loading from '../../components/Loading';
import CompanyPendingInvitations from './CompanyPendingInvitations';
import {
  useRemoveUserFromParentOrgMutation,
  useGetUserMembershipsQuery,
} from '../../data/api';
import UpdateUserModal from './UpdateUserModal';
import PropTypes from 'prop-types';

const styles = {
  cardTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '24px',
    color: '#162523',
  },
  searchContainer: {
    marginBottom: '24px',
  },
  searchField: {
    display: 'flex',
    width: '250px',
    padding: '8px 12px',
    alignItems: 'center',
    gap: '12px',
    borderRadius: '8px',
    border: '1px solid #E1E5E4',
    backgroundColor: '#FFF',
  },
  searchInput: {
    border: 'none',
    outline: 'none',
    width: '100%',
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '140%',
    letterSpacing: '-0.14px',
    '&::placeholder': {
      color: '#9AA7A6',
    },
  },
  tableContainer: {
    boxShadow: 'none',
    '& .MuiTable-root': {
      borderCollapse: 'separate',
    },
  },
  tableHeader: {
    backgroundColor: 'transparent',
    '& .MuiTableCell-head': {
      color: '#819190',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '150%',
      letterSpacing: '-0.14px',
      borderBottom: '1px solid #E1E5E4',
      padding: '16px 24px',
      backgroundColor: 'transparent',
    },
  },
  tableRow: {
    backgroundColor: 'transparent !important',
    cursor: 'pointer',
    '&:nth-of-type(odd)': {
      backgroundColor: 'transparent !important',
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'transparent !important',
    },
    '& .MuiTableCell-root': {
      borderBottom: '1px solid #E1E5E4',
      padding: '16px 24px',
      backgroundColor: 'transparent !important',
    },
    '&:last-child .MuiTableCell-root': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: '#F2F4F4 !important',
    },
  },
  userCell: {
    display: 'flex',
    padding: '4px 0',
    flexDirection: 'column',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
  },
  userName: {
    color: '#000',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '130%',
    letterSpacing: '-0.14px',
    textTransform: 'capitalize',
  },
  userEmail: {
    color: '#819190',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '140%',
    letterSpacing: '-0.12px',
  },
  statusText: {
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '130%',
    letterSpacing: '-0.14px',
  },
  clientsText: {
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '130%',
    letterSpacing: '-0.14px',
  },
  rolesText: {
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '130%',
    letterSpacing: '-0.14px',
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '16px',
    '& .MuiPagination-ul': {
      '& .MuiPaginationItem-root': {
        color: '#819190',
        '&.Mui-selected': {
          backgroundColor: '#26A993',
          color: '#fff',
          '&:hover': {
            backgroundColor: '#26A993',
          },
        },
        '&:hover': {
          backgroundColor: '#f5f5f5',
        },
      },
    },
  },
};

// Helper function to format list with +N logic
const formatListWithCount = (items, limit = 2) => {
  if (!items || items.length === 0) return 'None';
  if (items.length <= limit) return items.join(', ');
  const visible = items.slice(0, limit).join(', ');
  const remaining = items.length - limit;
  return `${visible}, +${remaining}`;
};

// Custom hook for fetching parent org members
const useParentOrgMembers = (parentOrgId) => {
  const {fetchOrgMembers: fetchOrgMembersApi} = useAuthFrontendApis();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [userIds, setUserIds] = useState(null);

  const fetchParentOrgMembers = useCallback(
    async (page = 0, pageSize = 25, search = '') => {
      if (!parentOrgId) return;

      setIsLoading(true);
      setError(null);
      setUserIds(null);

      try {
        const response = await fetchOrgMembersApi(parentOrgId, {
          page_number: page,
          page_size: pageSize,
          email_search: search,
        });

        await response.handle({
          success: (responseData) => {
            setData(responseData);
            // Set userIds to trigger memberships query
            if (responseData.users && responseData.users.length > 0) {
              setUserIds(responseData.users.map((user) => user.user_id));
            } else {
              setUserIds([]);
            }
          },
          orgNotFound: () => {
            setError('Organization not found');
          },
          orgsNotEnabled: () => {
            setError('Organizations are not enabled');
          },
          unauthorized: () => {
            setError('You are not authorized to view this organization');
          },
          unexpectedOrUnhandled: (err) => {
            setError(err.user_facing_error || 'An unexpected error occurred');
          },
        });
      } catch (err) {
        setError('Failed to fetch organization members');
      } finally {
        setIsLoading(false);
      }
    },
    [parentOrgId, fetchOrgMembersApi]
  );

  return {
    isLoading,
    error,
    data,
    userIds,
    fetchParentOrgMembers,
  };
};

// Action menu component
const ActionMenu = ({
  orgId,
  orgMember,
  isSAMLEnabled,
  onMemberRemoved,
  onShowSnackbar,
}) => {
  const [removeUserFromParentOrg] = useRemoveUserFromParentOrgMutation();
  const {userClass} = useAuthInfo();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const open = Boolean(anchorEl);

  const isCurrentUser = orgMember.user_id === userClass?.userId;

  // Don't show menu if SAML is enabled, if it's the current user, or if currently removing
  if (isSAMLEnabled || isCurrentUser || isRemoving) {
    if (isRemoving) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
          }}
        >
          <Loading
            loading={true}
            containerProps={{height: 'auto', minHeight: 'auto'}}
            loadingProps={{size: '16'}}
          />
        </Box>
      );
    }
    return null;
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRemoveUser = async () => {
    const message = `Are you sure you want to permanently remove ${orgMember.email} from the parent organization and all their client organizations? This action cannot be undone.`;

    if (!window.confirm(message)) {
      handleClose();
      return;
    }

    setIsRemoving(true);
    try {
      const result = await removeUserFromParentOrg({
        orgId,
        userId: orgMember.user_id,
      });

      if (result.data) {
        onMemberRemoved();
        onShowSnackbar({
          message: `${orgMember.email} has been removed from the organization`,
          severity: 'success',
        });
      } else if (result.error) {
        onShowSnackbar({
          message:
            result.error?.data?.message ||
            'Failed to remove user from organization',
          severity: 'error',
        });
      }
    } catch (err) {
      onShowSnackbar({
        message: 'Failed to remove user from organization',
        severity: 'error',
      });
    } finally {
      setIsRemoving(false);
      handleClose();
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{
          color: '#162523',
          '&:hover': {
            backgroundColor: '#F2F4F4',
            color: '#162523',
          },
        }}
      >
        <MoreHorizontal size={20} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            border: '1px solid #E1E5E4',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '120px',
          },
        }}
      >
        <MenuItem
          onClick={handleRemoveUser}
          sx={{
            color: '#C71212',
            fontSize: '14px',
            fontWeight: 500,
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            '&:hover': {
              backgroundColor: '#FEF2F2',
            },
          }}
        >
          <Trash2 size={16} />
          Remove
        </MenuItem>
      </Menu>
    </>
  );
};

ActionMenu.propTypes = {
  orgId: PropTypes.string.isRequired,
  orgMember: PropTypes.shape({
    user_id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  isSAMLEnabled: PropTypes.bool.isRequired,
  onMemberRemoved: PropTypes.func.isRequired,
  onShowSnackbar: PropTypes.func.isRequired,
};

const ParentOrgMembers = React.forwardRef(({isSAMLEnabled = false}, ref) => {
  const {parentOrg} = useSelector((state) => state.session);
  const {isLoading, error, data, userIds, fetchParentOrgMembers} =
    useParentOrgMembers(parentOrg?.orgId);
  const {fetchPendingOrgInvites} = useAuthFrontendApis();

  const {data: membershipsData, isLoading: isLoadingMemberships} =
    useGetUserMembershipsQuery(
      {
        parentOrgId: parentOrg?.orgId,
        // eslint-disable-next-line camelcase
        user_ids: userIds || [],
      },
      {
        skip: !parentOrg?.orgId || !userIds || userIds.length === 0,
      }
    );

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasPendingInvites, setHasPendingInvites] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const pageSize = 25;

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // Check for pending invites
  useEffect(() => {
    if (!parentOrg?.orgId) return;

    const checkPendingInvites = async () => {
      try {
        const response = await fetchPendingOrgInvites(parentOrg.orgId, {
          page_number: 0,
          page_size: 1,
          email_search: '',
        });

        await response.handle({
          success: (responseData) => {
            setHasPendingInvites(responseData.total_count > 0);
          },
          orgNotFound: () => {
            setHasPendingInvites(false);
          },
          orgsNotEnabled: () => {
            setHasPendingInvites(false);
          },
          unauthorized: () => {
            setHasPendingInvites(false);
          },
          unexpectedOrUnhandled: () => {
            setHasPendingInvites(false);
          },
        });
      } catch (err) {
        setHasPendingInvites(false);
      }
    };

    checkPendingInvites();
  }, [parentOrg?.orgId, fetchPendingOrgInvites]);

  useEffect(() => {
    if (parentOrg) {
      fetchParentOrgMembers(page - 1, pageSize, debouncedSearch);
    }
  }, [page, debouncedSearch, fetchParentOrgMembers, parentOrg]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleShowSnackbar = useCallback(({message, severity}) => {
    setSnackbar({open: true, message, severity});
  }, []);

  const refreshPendingInvites = useCallback(async () => {
    if (!parentOrg?.orgId) return;

    try {
      const response = await fetchPendingOrgInvites(parentOrg.orgId, {
        page_number: 0,
        page_size: 1,
        email_search: '',
      });

      await response.handle({
        success: (responseData) => {
          setHasPendingInvites(responseData.total_count > 0);
          // Increment refreshTrigger to force CompanyPendingInvitations to refetch
          setRefreshTrigger((prev) => prev + 1);
        },
        orgNotFound: () => {
          setHasPendingInvites(false);
        },
        orgsNotEnabled: () => {
          setHasPendingInvites(false);
        },
        unauthorized: () => {
          setHasPendingInvites(false);
        },
        unexpectedOrUnhandled: () => {
          setHasPendingInvites(false);
        },
      });
    } catch (err) {
      setHasPendingInvites(false);
    }
  }, [parentOrg?.orgId, fetchPendingOrgInvites]);

  // Refresh members list
  const refreshMembers = useCallback(() => {
    if (parentOrg) {
      fetchParentOrgMembers(page - 1, pageSize, debouncedSearch);
    }
  }, [page, debouncedSearch, fetchParentOrgMembers, parentOrg]);

  const handleMemberRemoved = useCallback(() => {
    fetchParentOrgMembers(page - 1, pageSize, debouncedSearch);
  }, [page, debouncedSearch, fetchParentOrgMembers]);

  // Expose refresh function to parent via ref
  React.useImperativeHandle(
    ref,
    () => ({
      refreshPendingInvites,
      refreshMembers,
    }),
    [refreshPendingInvites, refreshMembers]
  );

  // Helper function to get user name from memberships data
  const getUserName = (userId) => {
    if (
      !membershipsData ||
      !membershipsData[userId] ||
      !membershipsData[userId].name
    ) {
      return null;
    }
    const {firstName, lastName} = membershipsData[userId].name;
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    return firstName || lastName || null;
  };

  // Helper function to get client organizations for a user (excluding parent org)
  const getUserClientOrgs = (userId) => {
    if (
      !membershipsData ||
      !membershipsData[userId] ||
      !membershipsData[userId].memberships
    ) {
      return [];
    }
    return membershipsData[userId].memberships
      .filter((membership) => membership.orgId !== parentOrg?.orgId)
      .map((membership) => membership.orgName || membership.orgId)
      .sort((a, b) => a.localeCompare(b));
  };

  // Helper function to get roles for a user across client orgs (excluding parent org)
  const getUserRoles = (userId) => {
    if (
      !membershipsData ||
      !membershipsData[userId] ||
      !membershipsData[userId].memberships
    ) {
      return [];
    }
    const roles = membershipsData[userId].memberships
      .filter((membership) => membership.orgId !== parentOrg?.orgId)
      .map((membership) => membership.role)
      .filter(Boolean);
    // Remove duplicates
    return [...new Set(roles)];
  };

  // Helper function to get full memberships for a user
  const getUserMemberships = (userId) => {
    if (
      !membershipsData ||
      !membershipsData[userId] ||
      !membershipsData[userId].memberships
    ) {
      return [];
    }
    return membershipsData[userId].memberships;
  };

  const handleRowClick = (member, event) => {
    // Don't open modal if clicking on the action menu button
    if (
      event.target.closest('button') ||
      event.target.closest('[role="button"]')
    ) {
      return;
    }
    setSelectedUser(member);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const totalPages = data ? Math.ceil(data.total_count / pageSize) : 0;

  if (!parentOrg) {
    return (
      <Box sx={{paddingBottom: '80px'}}>
        <Alert severity="info">No parent organization found.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{paddingBottom: '80px'}}>
      {/* Search and Member Count */}
      <Box
        sx={{
          ...styles.searchContainer,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={styles.searchField}>
          <img src={SearchGreyIcon} height={20} width={20} alt="search" />
          <Box
            component="input"
            type="text"
            placeholder="Search users"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={styles.searchInput}
          />
          {search && (
            <CloseIcon
              sx={{
                fontSize: 16,
                color: '#9AA7A6',
                cursor: 'pointer',
                '&:hover': {
                  color: '#162523',
                },
              }}
              onClick={() => setSearch('')}
            />
          )}
        </Box>

        {/* Member Count */}
        {!isLoading && !error && data && (
          <Typography
            sx={{
              fontSize: '14px',
              color: '#819190',
              fontWeight: 500,
              transform: 'translate(-6px, 6px)',
            }}
          >
            {data.total_count} {data.total_count === 1 ? 'user' : 'users'}
          </Typography>
        )}
      </Box>

      {/* Pending Invitations - Only show if there are pending invites */}
      {hasPendingInvites && parentOrg && (
        <CompanyPendingInvitations
          company={{orgId: parentOrg.orgId, orgName: parentOrg.orgName}}
          debouncedSearch={debouncedSearch}
          onShowSnackbar={handleShowSnackbar}
          onInviteRevoked={refreshPendingInvites}
          refreshTrigger={refreshTrigger}
        />
      )}

      {/* Users Table */}
      <SettingsCard>
        {error && (
          <Alert severity="error" sx={{marginBottom: '16px'}}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {(isLoading || isLoadingMemberships) && (
          <Box sx={{width: '100%', display: 'flex', justifyContent: 'center'}}>
            <Loading
              loading={true}
              message="Loading users..."
              containerProps={{
                height: '300px',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 'none',
              }}
            />
          </Box>
        )}

        {/* Members Table */}
        {!isLoading && !isLoadingMemberships && !error && (
          <>
            <TableContainer sx={styles.tableContainer}>
              <Table>
                <TableHead sx={styles.tableHeader}>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Clients</TableCell>
                    <TableCell>Roles</TableCell>
                    <TableCell>Status</TableCell>
                    {!isSAMLEnabled && (
                      <TableCell
                        sx={{width: '48px', padding: '16px 8px'}}
                      ></TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.users?.length === 0 ? (
                    <TableRow sx={styles.tableRow}>
                      <TableCell
                        colSpan={!isSAMLEnabled ? 5 : 4}
                        align="center"
                      >
                        <Typography variant="body2" color="textSecondary">
                          No organization members found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.users?.map((member) => {
                      const clientOrgs = getUserClientOrgs(member.user_id);
                      const userRoles = getUserRoles(member.user_id);
                      const displayName =
                        getUserName(member.user_id) ||
                        member.email.split('@')[0];

                      return (
                        <TableRow
                          key={member.user_id}
                          sx={styles.tableRow}
                          onClick={(e) => handleRowClick(member, e)}
                        >
                          <TableCell>
                            <Box sx={styles.userCell}>
                              <Typography sx={styles.userName}>
                                {displayName}
                              </Typography>
                              <Typography sx={styles.userEmail}>
                                {member.email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography sx={styles.clientsText}>
                              {formatListWithCount(clientOrgs, 2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={styles.rolesText}>
                              {formatListWithCount(userRoles, 2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={styles.statusText}>
                              {member.is_enabled ? 'Active' : 'Inactive'}
                            </Typography>
                          </TableCell>
                          {!isSAMLEnabled && (
                            <TableCell
                              sx={{width: '48px', padding: '16px 8px'}}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ActionMenu
                                orgId={parentOrg.orgId}
                                orgMember={member}
                                isSAMLEnabled={isSAMLEnabled}
                                onMemberRemoved={handleMemberRemoved}
                                onShowSnackbar={handleShowSnackbar}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={styles.paginationContainer}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </SettingsCard>

      {/* Snackbar for success and error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        style={{marginTop: '35px'}}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
          severity={snackbar.severity}
          icon={
            snackbar.severity === 'success' ? (
              <Box
                sx={{
                  backgroundColor: '#26A993',
                  borderRadius: '50%',
                  width: 22,
                  height: 22,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CheckIcon sx={{color: 'white', fontSize: 16}} />
              </Box>
            ) : (
              <ErrorOutlineIcon sx={{color: '#D73E3E', fontSize: 22}} />
            )
          }
          sx={{
            width: '100%',
            backgroundColor: '#FFFFFF',
            color: '#000000',
            border: '1px solid #E1E5E4',
            borderRadius: '8px',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Update User Modal */}
      <UpdateUserModal
        open={isModalOpen}
        onClose={handleCloseModal}
        user={selectedUser}
        memberships={
          selectedUser ? getUserMemberships(selectedUser.user_id) : []
        }
        userData={
          selectedUser &&
          membershipsData &&
          membershipsData[selectedUser.user_id]
            ? membershipsData[selectedUser.user_id]
            : null
        }
        parentOrgId={parentOrg?.orgId}
      />
    </Box>
  );
});

ParentOrgMembers.displayName = 'ParentOrgMembers';

ParentOrgMembers.propTypes = {
  isSAMLEnabled: PropTypes.bool,
};

export default ParentOrgMembers;
